import request from '#/utils/request';
import { keysToSnake } from '#/api/adapters/wireCase';
import { hydrateJsonBlobError } from '#/utils/blobResponseError';

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') {
    return null;
  }

  const utf8Match = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, '');
    }
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch?.[1]?.trim() || null;
}

async function downloadExcelBlob(requestPromise) {
  try {
    const response = await requestPromise;
    const disposition = response.headers?.['content-disposition']
      ?? response.headers?.['Content-Disposition'];
    const filename = parseFilenameFromContentDisposition(disposition)
      || 'sales-order.xls';

    return {
      blob: response.data,
      filename,
    };
  } catch (error) {
    await hydrateJsonBlobError(error);
    throw error;
  }
}

/**
 * 下載銷貨單 Excel（.xls Blob；僅產品主列）。
 * `GET /crm/sales-order/{id}/excel`；`hideAmounts === true` 時帶 `hide_amounts=true`。
 *
 * @param {string} id
 * @param {{ hideAmounts?: boolean }} [options]
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export async function downloadSalesOrderExcel(id, { hideAmounts } = {}) {
  const params = {};
  if (hideAmounts === true) {
    params.hide_amounts = true;
  }

  return downloadExcelBlob(
    request.get(`/crm/sales-order/${id}/excel`, {
      responseType: 'blob',
      params,
    }),
  );
}

/**
 * 選列下載銷貨單 Excel（.xls Blob）。
 * `POST /crm/sales-order/{id}/excel`；body：`hideAmounts`、`lineOptions[]`
 *（`salesOrderLineId`／`printMtrSnapshotIds`／`printExtraItemIds`）。
 *
 * @param {string} id
 * @param {{
 *   hideAmounts?: boolean,
 *   lineOptions?: Array<{
 *     salesOrderLineId: string,
 *     printMtrSnapshotIds?: string[],
 *     printExtraItemIds?: string[],
 *   }>,
 * }} [payload]
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export async function downloadSalesOrderExcelWithSelection(id, payload = {}) {
  const lineOptions = (payload.lineOptions ?? []).map((line) => ({
    salesOrderLineId: line.salesOrderLineId,
    printMtrSnapshotIds: line.printMtrSnapshotIds ?? [],
    printExtraItemIds: line.printExtraItemIds ?? [],
  }));

  return downloadExcelBlob(
    request.post(
      `/crm/sales-order/${id}/excel`,
      keysToSnake({
        hideAmounts: payload.hideAmounts === true,
        lineOptions,
      }),
      { responseType: 'blob' },
    ),
  );
}
